import React, { useEffect, useState } from "react";
import Layout1 from "../../DataLayouts/Layout1";
import FormatAlignJustifyIcon from "@mui/icons-material/FormatAlignJustify";
import { MAIN_URL } from "../../../Configurations/Urls";
import DateRangeIcon from "@mui/icons-material/DateRange";
import PersonIcon from "@mui/icons-material/Person";
import CategoryIcon from "@mui/icons-material/Category";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import toast from "react-hot-toast";
import axios from "axios";
import { fetchAttendanceReport } from "../../../Apis/Attendance";
import { Box, Chip, Typography } from "@mui/material";

function RecordWithoutBreakList() {
  const [leaves, setLeaves] = useState([]);
  const { userData } = useAuthStore();
  const [summary, setSummary] = useState({
    date: null,
    total: 0,
    present: 0,
    absent: 0,
  });
  const org = userData?.organization;
  const [loading, setLoading] = useState(true);

  const formatTime = (dateTime) => {
    if (!dateTime) return "-";
    const date = new Date(dateTime);
    return date.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const calculateTotalWorkMinutes = (clockIn, clockOut, breakMinutes) => {
    if (!clockIn || !clockOut) return 0;
    const start = new Date(clockIn);
    const end = new Date(clockOut);
    const diffMs = end - start;
    const totalMinutes = Math.floor(diffMs / (1000 * 60));
    return totalMinutes - (breakMinutes || 0);
  };

  const formatMinutesToHours = (minutes) => {
    if (!minutes || minutes === 0) return "0 hrs";
    const hrs = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hrs} hrs ${mins} mins` : `${hrs} hrs`;
  };

  const statusChip = (status) => {
    const normalized = String(status || "").toLowerCase();
    const isPresent = normalized === "present";
    const isAbsent = normalized === "absent";
    return (
      <Chip
        size="small"
        label={status || "-"}
        sx={{
          fontWeight: 600,
          minWidth: 68,
          height: 22,
          fontSize: "0.75rem",
          color: isPresent ? "#0f7b3a" : isAbsent ? "#b42318" : "#344054",
          backgroundColor: isPresent
            ? "rgba(15, 123, 58, 0.12)"
            : isAbsent
              ? "rgba(180, 35, 24, 0.12)"
              : "rgba(152, 162, 179, 0.18)",
        }}
      />
    );
  };

  useEffect(() => {
    if (org?.organization_id) {
      setLoading(true);

      fetchAttendanceReport(org?.organization_id)
        .then((data) => {
          const a = data?.attendance_records || [];

          const b = a.map((item) => {
            calculateTotalWorkMinutes(
              item?.clock_in_time,
              item?.clock_out_time,
              item?.break_duration_minutes
            );

            return {
              ...item,
              id: item?.organization_person_attendance_record_id,
              employee_name: `${item?.employee_name || ""}`.trim(),
              status_type:
                item?.status_type?.attendance_status_type_name || "Present",
              check_in: formatTime(item?.clock_in_time),
              check_out: formatTime(item?.clock_out_time),
              workshift_total_work_minutes: formatMinutesToHours(
                item?.workshift_total_work_minutes
              ),
              actual_total_work_minutes: formatMinutesToHours(
                item?.actual_total_work_minutes
              ),
              attendance_date: item?.attendance_date,
              attendance_status_display: statusChip(item?.attendance_status),
            };
          });

          const sortedData = b.sort((a, b) => {
            const nameA = (a.employee_name || a.name || "").toLowerCase();
            const nameB = (b.employee_name || b.name || "").toLowerCase();
            return nameA.localeCompare(nameB);
          });

          setLeaves(sortedData);

          const date =
            sortedData?.length > 0 ? sortedData[0].attendance_date : null;
          const total = sortedData?.length;
          const present = sortedData.filter(
            (e) => e.attendance_status === "Present"
          )?.length;
          const absent = sortedData.filter(
            (e) => e.attendance_status === "Absent"
          )?.length;

          setSummary({ date, total, present, absent });
        })
        .catch((err) => {
          console.error("Failed to fetch employee Attendance record:", err);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [org]);

  const deleteemployeeleave = async (id) => {
    try {
      const org_id = org.organization_id;
      await axios.delete(
        `${MAIN_URL}/api/organizations/${org_id}/attendance-employee-record/${id}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      setLeaves((prev) => prev.filter((leave) => leave.id !== id));
      toast.success("Employee Attendance Record Deleted Successfully");
    } catch (error) {
      if (error.response && error.response.status === 401) {
        toast.error("Session Expired!");
        window.location.href = "/login";
      }
      console.error("Delete failed:", error);
      toast.error(
        error.response?.data?.error ||
          "Failed to delete Employee Attendance Record"
      );
    }
  };

  const summaryCardSx = {
    flex: "1 1 0",
    minWidth: 0,
    px: 2,
    py: 1.5,
    borderRadius: 2,
    border: "1px solid",
    borderColor: "divider",
    backgroundColor: "#fff",
  };

  return (
    <Layout1
      loading={loading}
      add_action={"ADD_ACTION"}
      edit_action={"EDIT_ACTION"}
      rowsPerPage={10}
      hideViewToggle
      dense
      heading={
        <Box sx={{ width: "100%" }}>
          <Box sx={{ fontSize: "1.5rem", fontWeight: 700, mb: 1.5, lineHeight: 1.3 }}>
            Daily Attendance Report
          </Box>
          <Box
            sx={{
              display: "flex",
              flexWrap: { xs: "wrap", sm: "nowrap" },
              gap: 1.5,
              width: "100%",
            }}
          >
            <Box sx={summaryCardSx}>
              <Typography variant="caption" color="text.secondary">
                Date
              </Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {summary.date || "-"}
              </Typography>
            </Box>
            <Box sx={summaryCardSx}>
              <Typography variant="caption" color="text.secondary">
                Total Employees
              </Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {summary.total}
              </Typography>
            </Box>
            <Box
              sx={{
                ...summaryCardSx,
                borderColor: "rgba(15, 123, 58, 0.25)",
                backgroundColor: "rgba(15, 123, 58, 0.06)",
              }}
            >
              <Typography variant="caption" sx={{ color: "#0f7b3a" }}>
                Present
              </Typography>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 700, color: "#0f7b3a" }}
              >
                {summary.present}
              </Typography>
            </Box>
            <Box
              sx={{
                ...summaryCardSx,
                borderColor: "rgba(180, 35, 24, 0.25)",
                backgroundColor: "rgba(180, 35, 24, 0.06)",
              }}
            >
              <Typography variant="caption" sx={{ color: "#b42318" }}>
                Absent
              </Typography>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 700, color: "#b42318" }}
              >
                {summary.absent}
              </Typography>
            </Box>
          </Box>
        </Box>
      }
      btnName={null}
      Data={leaves}
      delete_action={"DELETE_ACTION"}
      tableHeaders={[
        { name: "Emp Code", value_key: "employee_code", width: "12%" },
        {
          name: "Employee Name",
          value_key: "employee_name",
          textStyle: "capitalize",
          width: "28%",
        },
        { name: "Status", value_key: "attendance_status_display", width: "14%" },
        { name: "Clock In", value_key: "check_in", width: "15%" },
        { name: "Clock Out", value_key: "check_out", width: "15%" },
        { name: "Total Time", value_key: "actual_total_work_minutes", width: "16%" },
      ]}
      Icons={[
        <PersonIcon sx={{ fontSize: 60, color: "grey.500", mb: 2 }} />,
        <FormatAlignJustifyIcon color="primary" />,
        <CategoryIcon sx={{ color: "text.secondary" }} />,
        <DateRangeIcon sx={{ color: "text.secondary" }} />,
      ]}
      messages={["", "", "", ""]}
      Route={"/attendance/employee-recordss"}
      setData={setLeaves}
      DeleteFunc={deleteemployeeleave}
    />
  );
}

export default RecordWithoutBreakList;
